import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { parse } from 'twemoji-parser';

// types
import { PostEntities } from '@sprinklr/stories/post/Post';
import { NativeColorMode } from '../../../../widgets/PostsWidget/types';
import { NativeTextProps } from './types';

// components
import {
    StyledEmoji,
    StyledHashtag,
    StyledNativeText,
    StyledScreenName,
    StyledUrl,
} from './styledComponents';

// hooks
import { useClickActionContext } from 'components/Widget/context/ClickActionContext';

// helpers
import generateFragments, { getFormattedFragment } from '../../../../utils/EntityUtils/EntityUtils';

const NativeTextFragment = observer(
    ({ fragment, removeUrls, clickableUrls, handleEntityClick }) => {
        const { type, value, url } = fragment;
        const formattedFragment = useMemo(
            () => getFormattedFragment(fragment, clickableUrls, handleEntityClick),
            [fragment, clickableUrls, handleEntityClick]
        );
        switch (type) {
            case 'hashtag':
                return (
                    <StyledHashtag data-testid='post-text-entity-hashtag'>
                        {formattedFragment}
                    </StyledHashtag>
                );
            case 'emoji':
                return (
                    <>
                        <span>{` `}</span>
                        <StyledEmoji src={url} data-testid='post-text-entity-emoji' />
                    </>
                );
            case 'screenName':
                return (
                    <StyledScreenName data-testid='post-text-entity-screenname'>
                        {formattedFragment}
                    </StyledScreenName>
                );
            case 'url':
                return removeUrls ? null : (
                    <StyledUrl data-testid='post-text-entity-url'>{formattedFragment}</StyledUrl>
                );
            default:
                return <span>{` ${value} `}</span>;
        }
    }
);

const NativeText: React.FC<NativeTextProps> = ({
    text,
    textEntities = [],
    rtl,
    language,
    removeUrls = true,
    clickableUrls = false,
    maxCharacters = 450,
    emoji = {
        platform: 'default',
    },
    channel,
    lineClamp = {
        enabled: true,
        lines: 10,
        hasMedia: {
            enabled: true,
            lines: 7,
        },
    },
    hasMedia,
    colorMode = 'light' as NativeColorMode,
}) => {
    const emojiMap = useMemo(() => parse(text), [text]);
    const emojiEntities: PostEntities[] = useMemo(
        () =>
            emojiMap && emojiMap.length
                ? emojiMap.map(item => {
                      return {
                          indices: item.indices,
                          [item.type]: item.url,
                          source: item.text,
                      };
                  })
                : [],
        [emojiMap]
    );
    const entitiesMerged: PostEntities[] = useMemo(
        () =>
            emoji && emoji?.platform === 'twitter'
                ? [...(!!textEntities ? textEntities : []), ...emojiEntities]
                : textEntities,
        [emoji, emojiEntities, textEntities]
    );
    const fragments = useMemo(
        () =>
            generateFragments({
                text,
                textEntities: entitiesMerged,
                maxCharacters,
                channel,
            }),
        [text, entitiesMerged, maxCharacters, channel]
    );

    const { onEntityClick } = useClickActionContext();

    const handleEntityClick = useCallback(
        (event, fragment) => {
            if (onEntityClick) {
                onEntityClick({ event, url: fragment.url });
            }
        },
        [onEntityClick]
    );

    return (
        <StyledNativeText
            colorMode={colorMode}
            data-id='native-text'
            noGrow
            dir={rtl ? 'rtl' : undefined}
            rtl={rtl}
            lang={language}
            lineClamp={hasMedia && lineClamp.hasMedia ? lineClamp.hasMedia : lineClamp}
            maxCharacters={maxCharacters}
            size={hasMedia ? 's' : 'l'}>
            {fragments.map((fragment, index) => {
                return (
                    <NativeTextFragment
                        key={index}
                        fragment={fragment}
                        removeUrls={removeUrls}
                        clickableUrls={clickableUrls}
                        handleEntityClick={handleEntityClick}
                    />
                );
            })}
        </StyledNativeText>
    );
};

NativeText.displayName = 'NativeText';
export default observer(NativeText);
